import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Advice.css';

const CATEGORY_KEYWORDS = [
  ['Understanding estimates', ['estimate', 'treatment plan', 'range', 'quote', 'spread', 'low', 'high', 'transparent']],
  ['Common procedures', ['procedure', 'x-ray', 'blood', 'urinalysis', 'vaccination', 'medication']],
  ['Emergency care', ['emergency', 'urgent', 'after hours']],
  ['Dental care', ['dental', 'teeth', 'cleaning']],
  ['Surgery questions', ['surgery', 'anesthesia', 'cystotomy', 'spay', 'neuter']],
  ['Pet insurance', ['insurance', 'claim', 'reimbursement']],
  ['How to compare hospitals', ['compare', 'hospital', 'price', 'cost']],
  ['Privacy and uploading', ['privacy', 'redact', 'personal information', 'upload']]
];

const STARTER_GUIDES = [
  {
    _id: 'starter-estimates',
    title: 'How to read a vet estimate',
    category: 'Understanding estimates',
    content: 'Vet estimates often include a range, optional line items, and bundled services. Start by comparing the procedure name, diagnostics, anesthesia, medications, and follow-up care before comparing the final total.',
    createdAt: new Date().toISOString(),
    isStarter: true
  },
  {
    _id: 'starter-bill-vs-estimate',
    title: 'Bill vs estimate: what is the difference?',
    category: 'Understanding estimates',
    content: 'An estimate is a projected cost before care happens. A bill is the final amount after care is delivered. Bills are stronger evidence for actual prices, while estimates are useful for planning and comparing expected ranges.',
    createdAt: new Date().toISOString(),
    isStarter: true
  },
  {
    _id: 'starter-surgery-questions',
    title: 'Questions to ask before a surgery estimate',
    category: 'Surgery questions',
    content: 'Ask whether diagnostics, anesthesia, monitoring, pain medication, hospitalization, follow-up visits, and possible complications are included. These details can make two surgery estimates look very different.',
    createdAt: new Date().toISOString(),
    isStarter: true
  },
  {
    _id: 'starter-redaction',
    title: 'What to redact before uploading',
    category: 'Privacy and uploading',
    content: 'Before sharing a bill or estimate, cover owner names, phone numbers, addresses, account numbers, signatures, payment details, and anything else you would not want public.',
    createdAt: new Date().toISOString(),
    isStarter: true
  }
];

const PRACTICAL_GUIDES = [
  {
    _id: 'guide-cost-to-compare',
    title: 'Why it can cost money just to compare vet prices',
    category: 'How to compare hospitals',
    summary: 'Many hospitals need an exam, records, or diagnostics before giving a useful estimate. That can make price shopping expensive before treatment even starts.',
    steps: [
      'Ask what pricing information they can share before an exam.',
      'Use real uploaded bills and estimates to understand likely ranges before scheduling multiple visits.',
      'If diagnostics are required, ask whether results can be shared with another hospital for a second opinion.'
    ],
    actionLabel: 'Research prices first',
    actionLink: '/browse',
    isPractical: true
  },
  {
    _id: 'guide-compare-surgery',
    title: 'How to compare two surgery estimates',
    category: 'Surgery questions',
    summary: 'Surgery estimates can look wildly different because anesthesia, lab work, pain medication, monitoring, and follow-up care may be listed separately.',
    steps: [
      'Compare the named procedure first, not just the final total.',
      'Check whether anesthesia, diagnostics, medications, and hospitalization are included.',
      'Use bill-backed prices when available, then treat estimates as planning ranges.'
    ],
    actionLabel: 'Compare hospitals',
    actionLink: '/browse',
    isPractical: true
  },
  {
    _id: 'guide-ask-estimate',
    title: 'What to ask when an estimate feels high',
    category: 'How to compare hospitals',
    summary: 'A high estimate is not always wrong. It may include safer monitoring, optional diagnostics, or bundled follow-up care.',
    steps: [
      'Ask which line items are required and which are optional.',
      'Ask what could make the final bill higher than the estimate.',
      'Compare similar services from nearby hospitals before deciding.'
    ],
    actionLabel: 'Browse real prices',
    actionLink: '/browse',
    isPractical: true
  },
  {
    _id: 'guide-upload-safely',
    title: 'How to upload safely and still help others',
    category: 'Privacy and uploading',
    summary: 'Uploads are most valuable when they preserve prices and service names while removing personal details.',
    steps: [
      'Keep hospital name, date, service descriptions, and prices visible.',
      'Redact owner contact info, account numbers, payment details, and private notes.',
      'Review extracted services before saving so the data can be compared.'
    ],
    actionLabel: 'Upload a bill',
    actionLink: '/upload',
    isPractical: true
  },
  {
    _id: 'guide-estimate-price-spread',
    title: 'Why the low and high on a vet estimate can say a lot about a hospital',
    category: 'Understanding estimates',
    summary: 'Most vet estimates list a low and high price for each service, sometimes with the exact same quantity for both. That gap is not random — it is a window into how a hospital prices its care.',
    steps: [
      'Look at the low and high prices for each service line, not just the total. A $169/day vs $194/day spread for the same hospital stay means the vet can choose to charge anywhere in that range with no objective criteria.',
      'Pay attention when the quantity is identical for both low and high (e.g. 3 days low, 3 days high) but the unit price differs. This means the uncertainty is in the rate, not in how much care your pet will need.',
      'Compare the spread percentage across hospitals, not just the final totals. A hospital with a narrow spread (e.g. 5–8%) is being more transparent about what the care actually costs. A hospital with a wide spread (e.g. 20%+) is keeping more pricing flexibility — and will almost always bill at the high end.',
      'Ask the hospital directly: "What determines whether I am billed at the low or high rate?" A good hospital should have a clear answer. If the answer is vague, the high rate is likely the default.'
    ],
    actionLabel: 'Browse hospitals',
    actionLink: '/browse',
    isPractical: true
  }
];

const inferCategory = (post) => {
  if (post.category) {
    return post.category;
  }

  const text = `${post.title || ''} ${post.content || ''}`.toLowerCase();
  const match = CATEGORY_KEYWORDS.find(([, keywords]) => keywords.some(keyword => text.includes(keyword)));
  return match ? match[0] : 'General';
};

const getExcerpt = (content = '') => {
  const cleaned = content.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= 135) {
    return cleaned;
  }

  const excerpt = cleaned.slice(0, 135);
  return `${excerpt.slice(0, excerpt.lastIndexOf(' '))}...`;
};

const Advice = () => {
  const [posts, setPosts] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/posts')
      .then(response => response.json())
      .then(data => {
        setPosts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching posts:', error);
        setLoading(false);
      });
  }, []);

  const allArticles = useMemo(() => {
    const normalizedPosts = posts.map(post => ({ ...post, category: inferCategory(post) }));
    const practicalGuides = PRACTICAL_GUIDES.map(guide => ({
      ...guide,
      content: `${guide.summary} ${guide.steps.join(' ')}`
    }));
    return [...normalizedPosts, ...practicalGuides, ...STARTER_GUIDES];
  }, [posts]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(allArticles.map(article => article.category || 'General'))];
    return ['All', ...uniqueCategories.sort()];
  }, [allArticles]);

  const filteredArticles = allArticles.filter(article => {
    const searchText = `${article.title} ${article.content} ${article.category}`.toLowerCase();
    const matchesQuery = !query.trim() || searchText.includes(query.trim().toLowerCase());
    const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
    return matchesQuery && matchesCategory;
  });

  const starterArticles = filteredArticles.filter(article => article.isStarter);
  const practicalGuides = filteredArticles.filter(article => article.isPractical);
  const publishedArticles = filteredArticles.filter(article => !article.isStarter);
  const adminArticles = publishedArticles.filter(article => !article.isPractical);

  return (
    <main className="advice-page">
      <section className="advice-shell">
        <section className="advice-hero">
          <div className="advice-header">
            <p>VetBill Advice</p>
            <h1>Understand vet bills before you compare them</h1>
            <span>Plain-language guides for reading estimates, comparing hospitals, and sharing documents safely.</span>
          </div>
          <div className="advice-hero-card">
            <span>Start here</span>
            <strong>New to comparing vet prices?</strong>
            <p>Begin with the basics, then browse hospitals once you know what to look for.</p>
            <div>
              <Link to="/browse" className="advice-primary-link">Browse hospitals</Link>
              <Link to="/upload" className="advice-secondary-link">Upload a bill</Link>
            </div>
          </div>
        </section>

        <section className="advice-controls" aria-label="Search advice articles">
          <div>
            <h2>Find a guide</h2>
            <p>Search by topic or choose a category that matches the question you have.</p>
          </div>
          <label>
            Search
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search estimates, surgery, dental, privacy..."
            />
          </label>
          <label>
            Category
            <select value={selectedCategory} onChange={event => setSelectedCategory(event.target.value)}>
              {categories.map(category => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
        </section>

        {loading && <div className="advice-empty">Loading advice...</div>}

        {!loading && !filteredArticles.length && (
          <div className="advice-empty">No articles match that search yet.</div>
        )}

        {!loading && Boolean(practicalGuides.length) && (
          <section className="advice-section advice-practical-section">
            <div className="advice-section-heading">
              <div>
                <h2>Practical guides</h2>
                <p>Use these when you are deciding what to compare, what to ask, or what to upload next.</p>
              </div>
              <span>{practicalGuides.length} guides</span>
            </div>
            <div className="advice-practical-grid">
              {practicalGuides.map(guide => (
                <article key={guide._id} className="advice-practical-card">
                  <span>{guide.category}</span>
                  <h3>{guide.title}</h3>
                  <p>{guide.summary}</p>
                  <ol>
                    {guide.steps.map(step => <li key={step}>{step}</li>)}
                  </ol>
                  <Link to={guide.actionLink} className="advice-secondary-link">{guide.actionLabel}</Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {!loading && Boolean(starterArticles.length) && (
          <section className="advice-section">
            <div className="advice-section-heading">
              <div>
                <h2>Quick answers</h2>
                <p>Short explanations shown in full here, so you do not need to open another page.</p>
              </div>
              <span>{starterArticles.length} guides</span>
            </div>
            <div className="advice-grid advice-guide-grid">
              {starterArticles.map(article => (
                <article key={article._id} className="advice-card advice-guide-card">
                  <div className="advice-card-content advice-quick-answer">
                    <span>{article.category}</span>
                    <h3>{article.title}</h3>
                    <p>{article.content}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {!loading && Boolean(adminArticles.length) && (
          <section className="advice-section">
            <div className="advice-section-heading">
              <div>
                <h2>Articles and how-tos</h2>
                <p>More detailed guidance for navigating vet care, costs, and conversations with hospitals.</p>
              </div>
              <span>{adminArticles.length} articles</span>
            </div>
            <div className="advice-grid">
              {adminArticles.map(article => (
                <article key={article._id} className="advice-card">
                  <div className="advice-card-content">
                    <span>{article.category}</span>
                    <h3>{article.title}</h3>
                    <p>{getExcerpt(article.content)}</p>
                    <Link to={`/post/${article._id}`} className="read-more">Read article</Link>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="advice-contribution-cta">
          <div>
            <h2>Help make vet pricing easier to understand</h2>
            <p>Every uploaded bill or estimate makes VetBill more useful for the next pet owner comparing care.</p>
          </div>
          <Link to="/upload" className="advice-primary-link">Upload a bill or estimate</Link>
        </section>
      </section>
    </main>
  );
};

export default Advice;
